import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db } from '../utils/init-firebase';
import { collection, addDoc, query, onSnapshot, serverTimestamp, where, doc, getDoc, getDocs, orderBy, deleteDoc, limit } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import {
    Box,
    Flex,
    Heading,
    Text,
    Select,
    Button,
    VStack,
    HStack,
    Icon,
    useColorModeValue,
    Spinner,
    Center,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Image,
    IconButton,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Avatar,
} from '@chakra-ui/react';
import { BarChart2, PlusCircle, Trophy, Settings, Trash2 } from 'lucide-react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';

// --- Reusable UI Components ---
const LoadingSpinner = () => <Center h="100%" w="100%"><Spinner size="xl" color="blue.500" /></Center>;
const Card = ({ children, ...props }) => {
    const cardBg = useColorModeValue('white', 'gray.800');
    return (<Box bg={cardBg} rounded="xl" shadow="lg" p={6} {...props}>{children}</Box>);
}

// --- Fallback Activities ---
const FALLBACK_ACTIVITY_POINTS = {
    'OUTBOUND_CALL_VM': { label: 'Outbound Call (Voicemail)', points: 2 },
    'HANDWRITTEN_CARD': { label: 'Handwritten Card Mailed', points: 2 },
    'OUTBOUND_CALL_CONTACT': { label: 'Outbound Call (Contact Made)', points: 5 },
    'SOCIAL_MEDIA_VIDEO': { label: 'Business Video on Social Media', points: 5 },
    'NETWORKING_EVENT': { label: 'Networking Event Attended', points: 5 },
    'RE_OFFICE_STOPBY': { label: 'Stopped by Real Estate Office', points: 10 },
    'SMALL_BIZ_STOPBY': { label: 'Stopped by Small Business', points: 10 },
};


// --- Header Component ---
const Header = ({ challenge }) => {
    const headingColor = useColorModeValue('gray.800', 'white');
    const textColor = useColorModeValue('gray.600', 'gray.300');

    return (
        <Flex direction="column" align="center" mb={8} textAlign="center">
            <Image 
                src='https://fhl.one/static/media/logo-color.bd3dba21014a632920ed.png' 
                alt="Future Home Loans Logo" 
                h="75px" 
                w="200px" 
                objectFit="contain" 
                mb={2} 
            />
            <Heading as="h1" size="2xl" fontWeight="bold" color={headingColor}>
                {challenge ? challenge.name : 'Sales Challenge'}
            </Heading>
            {challenge && (
                <Text color={textColor} mt={2}>
                    {moment(challenge.startDate).format('MMMM Do, YYYY')} - {moment(challenge.endDate).format('MMMM Do, YYYY')}
                </Text>
            )}
        </Flex>
    );
};

// --- Reusable Delete Confirmation Dialog ---
function DeleteActivityDialog({ isOpen, onClose, onDelete, activityLabel }) {
    const cancelRef = React.useRef();
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDelete = async () => {
        setIsDeleting(true);
        await onDelete();
        setIsDeleting(false);
        onClose();
    };

    return (
        <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={onClose}
        >
            <AlertDialogOverlay>
                <AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                        Delete Activity
                    </AlertDialogHeader>
                    <AlertDialogBody>
                        Are you sure you want to delete the activity: "{activityLabel}"? This action cannot be undone.
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelRef} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button colorScheme="red" onClick={handleDelete} ml={3} isLoading={isDeleting}>
                            Delete
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
}


// --- Core Feature Components ---
function ActivityLogger({ currentUser, toast, challengeId, activityConfig }) {
    const [activity, setActivity] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const selectBg = useColorModeValue('white', 'gray.700');
    const headingColor = useColorModeValue('gray.800', 'white');

    const currentActivities = activityConfig || FALLBACK_ACTIVITY_POINTS;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!currentUser || !challengeId) {
            toast({ title: "Error", description: "Cannot log activity without a selected challenge.", status: "error", duration: 3000, isClosable: true });
            return;
        }
        setIsSubmitting(true);

        const selectedActivity = currentActivities[activity];

        const activityData = {
            userId: currentUser.uid,
            userName: currentUser.displayName,
            userPhoto: currentUser.photoURL,
            activityType: activity,
            points: selectedActivity.points,
            timestamp: serverTimestamp(),
            challengeId: challengeId
        };

        try {
            await addDoc(collection(db, 'salesChallengeActivities'), activityData);
            toast({ title: 'Success!', description: `Logged "${selectedActivity.label}".`, status: 'success', duration: 3000, isClosable: true });
            setActivity(''); // Reset dropdown after submission
        } catch (error) {
            console.error("Error logging activity: ", error);
            toast({ title: 'Error', description: "Could not log activity.", status: 'error', duration: 3000, isClosable: true });
        } finally {
            setIsSubmitting(false);
        }
    };
    
    return (
        <Card>
            <HStack mb={4}>
                <Icon as={PlusCircle} w={6} h={6} color="green.500" />
                <Heading as="h3" size="md" color={headingColor}>Log New Activity</Heading>
            </HStack>
            <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                    <Select value={activity} onChange={(e) => setActivity(e.target.value)} bg={selectBg} placeholder="Select an activity...">
                        {Object.entries(currentActivities)
                            .sort(([,a],[,b]) => a.points - b.points)
                            .map(([key, value]) => (
                            <option key={key} value={key}>{value.label} ({value.points} pts)</option>
                        ))}
                    </Select>
                    <Button type="submit" colorScheme="green" w="full" isLoading={isSubmitting} loadingText="Logging..." isDisabled={!activity || isSubmitting}>Log Activity</Button>
                </VStack>
            </form>
        </Card>
    );
}

function Leaderboard({ activities, timeFrame }) {
    const firstPlaceBg = useColorModeValue('yellow.100', 'yellow.900/50');
    const otherPlaceBg = useColorModeValue('gray.50', 'gray.700/50');
    const textColor = useColorModeValue('gray.500', 'gray.400');
    const pointsColor = useColorModeValue('blue.600', 'blue.400');

    const leaderboardData = useMemo(() => {
        const scores = {};
        activities.forEach(activity => {
            scores[activity.userName] = (scores[activity.userName] || 0) + activity.points;
        });
        return Object.entries(scores)
            .map(([name, points]) => ({ name, points }))
            .sort((a, b) => b.points - a.points);
    }, [activities]);

    return (
        <Card h="100%">
            <Flex justify="center" align="center" mb={4}>
                <Box
                    p={4}
                    borderRadius="lg"
                    bgGradient="linear(to-r, blue.400, teal.400)"
                    color="white"
                    shadow="lg"
                    transition="all 0.3s"
                    _hover={{
                        bgGradient: "linear(to-r, blue.500, teal.500)",
                        shadow: "xl",
                        transform: "translateY(-2px)"
                    }}
                >
                    <HStack>
                        <Icon as={Trophy} w={8} h={8} color="yellow.300" />
                        <Heading 
                            as="h3" 
                            size="lg" 
                            fontSize={{ base: 'md', lg: 'lg' }}
                            textShadow="1px 1px 3px rgba(0,0,0,0.3)"
                            whiteSpace="nowrap"
                        >
                            Leaderboard - {timeFrame}
                        </Heading>
                    </HStack>
                </Box>
            </Flex>
            <Box h="calc(100% - 60px)" overflowY="auto">
                {leaderboardData.length > 0 ? (
                    leaderboardData.map((broker, index) => (
                        <Flex key={broker.name} justify="space-between" align="center" p={4} rounded="lg" bg={index === 0 ? firstPlaceBg : otherPlaceBg} my={2}>
                            <HStack>
                                <Text fontWeight="bold" fontSize="lg" w={8}>{index + 1}{index === 0 && <Icon as={Trophy} ml={1} color="yellow.400" />}</Text>
                                <Text fontWeight="medium" fontSize="lg">{broker.name}</Text>
                            </HStack>
                            <Text fontWeight="bold" fontSize="lg" color={pointsColor}>{broker.points} pts</Text>
                        </Flex>
                    ))
                ) : (
                    <Text color={textColor} textAlign="center" py={4}>No activities logged for this period.</Text>
                )}
            </Box>
        </Card>
    );
}

function MyActivityFeed({ activities, currentUser, handleDeleteActivity, activityConfig }) {
    const { isOpen: isViewAllOpen, onOpen: onViewAllOpen, onClose: onViewAllClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    
    const [activityToDelete, setActivityToDelete] = useState(null);

    const headingColor = useColorModeValue('gray.800', 'white');
    const itemBg = useColorModeValue('gray.50', 'gray.700/50');

    const myActivities = useMemo(() => {
        if (!currentUser?.uid) return [];
        return activities
            .filter(act => act.userId === currentUser.uid)
            .sort((a, b) => (b.timestamp?.toDate() || 0) - (a.timestamp?.toDate() || 0));
    }, [activities, currentUser.uid]);

    const recentActivities = myActivities.slice(0, 5);

    const handleDeleteClick = (activity) => {
        setActivityToDelete(activity);
        onDeleteOpen();
    };

    const confirmDelete = () => {
        if (activityToDelete) {
            handleDeleteActivity(activityToDelete.id);
        }
    };
    
    const ActivityItem = ({ act, isFullView = false }) => {
        const currentActivities = activityConfig || FALLBACK_ACTIVITY_POINTS;
        return (
            <Flex key={act.id} justify="space-between" align="center" bg={itemBg} p={3} rounded="lg">
                <Box>
                    <Text fontWeight="medium">{currentActivities[act.activityType]?.label}</Text>
                    <Text fontSize="xs" color="gray.500">
                        {act.timestamp ? (isFullView ? moment(act.timestamp.toDate()).toLocaleString() : moment(act.timestamp.toDate()).calendar()) : 'Just now'}
                    </Text>
                </Box>
                <HStack>
                    <Text fontWeight="bold" color="green.400">+{act.points} pts</Text>
                    <IconButton
                        icon={<Icon as={Trash2} />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Delete activity"
                        onClick={() => handleDeleteClick(act)}
                    />
                </HStack>
            </Flex>
        );
    };

    return (
        <Card>
            <HStack mb={4}>
                <Icon as={BarChart2} w={6} h={6} color="indigo.500" />
                <Heading as="h3" size="md" color={headingColor}>My Recent Activities</Heading>
            </HStack>
            <VStack spacing={3} align="stretch">
                {recentActivities.length > 0 ? (
                    recentActivities.map(act => <ActivityItem act={act} key={`recent-${act.id}`} />)
                ) : (
                    <Text color="gray.500" textAlign="center" py={4}>You haven't logged any activities yet.</Text>
                )}
            </VStack>
            <Button mt={4} w="full" variant="outline" onClick={onViewAllOpen}>View All Activity</Button>

            <Modal isOpen={isViewAllOpen} onClose={onViewAllClose} size="xl" scrollBehavior="inside">
                <ModalOverlay /><ModalContent><ModalHeader>My Full Activity History</ModalHeader><ModalCloseButton />
                <ModalBody><VStack spacing={3} align="stretch">
                    {myActivities.length > 0 ? (
                        myActivities.map(act => <ActivityItem act={act} key={`all-${act.id}`} isFullView />)
                    ) : (
                        <Text>No activities logged.</Text>
                    )}
                </VStack></ModalBody>
                <ModalFooter><Button colorScheme="blue" onClick={onViewAllClose}>Close</Button></ModalFooter>
                </ModalContent>
            </Modal>
            
            {activityToDelete && (
                 <DeleteActivityDialog
                    isOpen={isDeleteOpen}
                    onClose={() => {
                        setActivityToDelete(null);
                        onDeleteClose();
                    }}
                    onDelete={confirmDelete}
                    activityLabel={(activityConfig || FALLBACK_ACTIVITY_POINTS)[activityToDelete.activityType]?.label}
                />
            )}
        </Card>
    );
}

// --- Challenge Activity Log Component ---
const ChallengeActivityLog = ({ activities, activityConfig }) => {
    const headingColor = useColorModeValue('gray.700', 'white');
    const itemBg = useColorModeValue('gray.50', 'gray.700');
    const currentActivities = activityConfig || FALLBACK_ACTIVITY_POINTS;

    const sortedActivities = useMemo(() => {
        return [...activities].sort((a, b) => (b.timestamp?.toDate() || 0) - (a.timestamp?.toDate() || 0));
    }, [activities]);

    return (
        <Card h="100%">
            <Heading size="md" color={headingColor} mb={4}>Challenge Activity Feed</Heading>
            <VStack spacing={3} align="stretch" overflowY="auto" maxH="75vh">
                {sortedActivities.length > 0 ? sortedActivities.map(activity => (
                    <Box key={activity.id} p={3} bg={itemBg} borderRadius="md">
                        <HStack>
                            <Avatar size="sm" name={activity.userName} src={activity.userPhoto} />
                            <VStack align="start" spacing={0}>
                                <Text fontSize="sm">
                                    <Text as="span" fontWeight="bold">{activity.userName}</Text> logged "{currentActivities[activity.activityType]?.label || 'an activity'}"
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                    {moment(activity.timestamp?.toDate()).fromNow()}
                                </Text>
                            </VStack>
                        </HStack>
                    </Box>
                )) : <Text color="gray.500">No activities logged in this challenge yet.</Text>}
            </VStack>
        </Card>
    );
};


// --- Helper Function ---
const generateWeeks = (challenge) => {
    if (!challenge || !challenge.startDate || !challenge.endDate) return [];

    const weeks = [];
    let current = moment(challenge.startDate).startOf('isoWeek'); 

    while (current.isSameOrBefore(moment(challenge.endDate))) {
        const weekStart = current.clone();
        const weekEnd = current.clone().endOf('isoWeek');

        weeks.push({
            label: `Week ${weeks.length + 1}: ${weekStart.format('MM/DD/YY')} - ${weekEnd.format('MM/DD/YY')}`,
            value: {
                start: weekStart.toDate(),
                end: weekEnd.toDate()
            }
        });
        current.add(1, 'week');
    }
    return [{ label: 'Overall', value: 'overall' }, ...weeks];
};


// --- Main Page Component ---
export default function SalesChallengePage() {
    const { currentUser, SuperAdmin } = useAuth();
    const history = useHistory();
    const toast = useToast();
    const bgColor = useColorModeValue('gray.50', 'gray.900');
    const selectBgColor = useColorModeValue('white', 'gray.700');
    
    const [allChallenges, setAllChallenges] = useState([]);
    const [selectedChallenge, setSelectedChallenge] = useState(null);
    const [activities, setActivities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [weekOptions, setWeekOptions] = useState([]);
    const [selectedWeek, setSelectedWeek] = useState({ label: 'Overall', value: 'overall' });

    useEffect(() => {
        const fetchInitialData = async () => {
            setIsLoading(true);
            try {
                const settingsDocRef = doc(db, 'settings', 'salesChallenge');
                const settingsDoc = await getDoc(settingsDocRef);
                const defaultId = settingsDoc.exists() ? settingsDoc.data().defaultChallengeId : null;

                const challengesQuery = query(collection(db, 'salesChallenges'), where('isHidden', '==', false), orderBy('startDate', 'desc'));
                const challengesSnapshot = await getDocs(challengesQuery);
                const challengesList = challengesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setAllChallenges(challengesList);
                
                if (defaultId && challengesList.some(c => c.id === defaultId)) {
                    setSelectedChallenge(challengesList.find(c => c.id === defaultId));
                } else if (challengesList.length > 0) {
                    setSelectedChallenge(challengesList[0]);
                } else {
                    setSelectedChallenge(null);
                }
            } catch (error) {
                console.error("Error fetching challenges:", error);
                toast({ title: "Error", description: "Could not load challenges.", status: "error" });
            }
        };
        fetchInitialData();
    }, [toast]);

    useEffect(() => {
        if (!selectedChallenge) {
            setActivities([]);
            setIsLoading(false);
            return;
        }

        const weeks = generateWeeks(selectedChallenge);
        setWeekOptions(weeks);
        setSelectedWeek(weeks[0]); // Default to "Overall"

        setIsLoading(true);
        const activitiesQuery = query(
            collection(db, 'salesChallengeActivities'),
            where('challengeId', '==', selectedChallenge.id)
        );
        const unsubscribe = onSnapshot(activitiesQuery, (snapshot) => {
            const activityList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setActivities(activityList);
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching activities:", error);
            toast({ title: "Error", description: `Could not load data for ${selectedChallenge.name}.`, status: "error" });
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [selectedChallenge, toast]);

    const filteredActivities = useMemo(() => {
        if (!selectedWeek || selectedWeek.value === 'overall') {
            return activities;
        }
    
        if (!activities || activities.length === 0) {
            return [];
        }
    
        const { start, end } = selectedWeek.value;
        const weekStart = moment(start).startOf('day');
        const weekEnd = moment(end).endOf('day');
    
        return activities.filter(activity => {
            if (!activity.timestamp) return false;
            const activityDate = moment(activity.timestamp.toDate());
            return activityDate.isBetween(weekStart, weekEnd, null, '[]');
        });
    }, [activities, selectedWeek]);


    const handleDeleteActivity = async (activityId) => {
        if (!activityId) return;
        try {
            await deleteDoc(doc(db, 'salesChallengeActivities', activityId));
            toast({
                title: "Activity Deleted",
                description: "Your activity has been successfully removed.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Error deleting activity:", error);
            toast({
                title: "Error",
                description: "Could not delete the activity. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    if (!currentUser) {
         return <Center h="80vh"><Text>Please log in to view the challenge.</Text></Center>;
    }

    return (
        <Box bg={bgColor} minH="100vh" p={{ base: 4, md: 8 }}>
            <Box maxW="8xl" mx="auto">
                <Flex justify="space-between" align="center" mb={4}>
                     <HStack>
                         {allChallenges.length > 1 && (
                             <Select 
                                 w="300px" 
                                 value={selectedChallenge?.id || ''} 
                                 onChange={(e) => setSelectedChallenge(allChallenges.find(c => c.id === e.target.value))}
                                 bg={selectBgColor}
                             >
                                 {allChallenges.map(c => (
                                     <option key={c.id} value={c.id}>
                                         {c.name} ({new Date(c.startDate).toLocaleDateString()} - {new Date(c.endDate).toLocaleDateString()})
                                     </option>
                                 ))}
                             </Select>
                         )}
                         {selectedChallenge && (
                             <Select
                                 w="300px"
                                 value={selectedWeek.label}
                                 onChange={(e) => {
                                     const selectedOpt = weekOptions.find(w => w.label === e.target.value);
                                     setSelectedWeek(selectedOpt);
                                 }}
                                 bg={selectBgColor}
                                 isDisabled={isLoading}
                             >
                                 {weekOptions.map(w => (
                                     <option key={w.label} value={w.label}>{w.label}</option>
                                 ))}
                             </Select>
                         )}
                     </HStack>
                    {SuperAdmin && (
                        <IconButton
                            aria-label="Challenge Settings"
                            icon={<Icon as={Settings} />}
                            onClick={() => history.push('/sales-challenge-settings')}
                        />
                    )}
                </Flex>

                <Header challenge={selectedChallenge} />
                
                {isLoading ? <LoadingSpinner /> : !selectedChallenge ? (
                    <Center><Text>No active challenges found.</Text></Center>
                ) : (
                    <main>
                        <Flex direction={{ base: 'column', xl: 'row' }} gap={8}>
                            <VStack spacing={8} w={{ base: '100%', xl: '25%' }}>
                                <ActivityLogger 
                                    currentUser={currentUser} 
                                    toast={toast} 
                                    challengeId={selectedChallenge.id}
                                    activityConfig={selectedChallenge.activityConfig}
                                />
                                <MyActivityFeed 
                                    activities={filteredActivities} 
                                    currentUser={currentUser} 
                                    handleDeleteActivity={handleDeleteActivity}
                                    activityConfig={selectedChallenge.activityConfig}
                                />
                            </VStack>
                            <Box w={{ base: '100%', xl: '50%' }}>
                                <Leaderboard activities={filteredActivities} timeFrame={selectedWeek.label} />
                            </Box>
                            <Box w={{ base: '100%', xl: '25%' }}>
                                <ChallengeActivityLog activities={filteredActivities} activityConfig={selectedChallenge.activityConfig} />
                            </Box>
                        </Flex>
                    </main>
                )}
            </Box>
        </Box>
    );
}
